import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Info } from 'lucide-react';

const Step6CreditHistory = ({ formData, onChange, errors = {} }) => {
  return (
    <div className="space-y-6">
      {/* Credit Track Record */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Credit & Repayment History</h3>
        <p className="text-sm text-gray-600">
          Provide information about past credit history and repayment behavior
        </p>

        {/* Full Repayment */}
        <div className="p-4 bg-blue-50 rounded-lg space-y-4">
          <h4 className="font-medium text-gray-900">Repayment Track Record</h4>
          <div className="space-y-2">
            <Label htmlFor="full_repayment">
              Full Repayment <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.full_repayment || ''}
              onValueChange={(value) => onChange('full_repayment', value)}
            >
              <SelectTrigger data-testid="full-repayment-select" className={errors.full_repayment ? 'border-red-500' : ''}>
                <SelectValue placeholder="Has the applicant fully repaid all past loans?" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              Has the applicant fully repaid all previous loans on time?
            </p>
            {errors.full_repayment && <p className="text-sm text-red-500">{errors.full_repayment}</p>}
          </div>

          {/* Display Info */}
          {formData.full_repayment && (
            <div className={`p-3 ${formData.full_repayment === 'Yes' ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'} border rounded-md`}>
              <div className="flex items-start gap-2">
                <Info className={`h-5 w-5 ${formData.full_repayment === 'Yes' ? 'text-green-600' : 'text-amber-600'} mt-0.5`} />
                <div>
                  <p className={`text-sm font-medium ${formData.full_repayment === 'Yes' ? 'text-green-900' : 'text-amber-900'}`}>
                    {formData.full_repayment === 'Yes' ? 'Strong repayment record' : 'Incomplete repayment history'}
                  </p>
                  <p className={`text-xs ${formData.full_repayment === 'Yes' ? 'text-green-600' : 'text-amber-600'} mt-1`}>
                    {formData.full_repayment === 'Yes'
                      ? 'Full repayment positively impacts credit assessment'
                      : 'Partial repayment may affect credit assessment'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div className="space-y-2">
            <Label htmlFor="prior_default_history">
              Prior Default History <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.prior_default_history || ''}
              onValueChange={(value) => onChange('prior_default_history', value)}
            >
              <SelectTrigger data-testid="prior-default-history-select" className={errors.prior_default_history ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select default status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="None">No Defaults</SelectItem>
                <SelectItem value="Yes - 1 time">Yes - 1 time</SelectItem>
                <SelectItem value="Yes - 2 times">Yes - 2 times</SelectItem>
                <SelectItem value="Yes - 3+ times">Yes - 3+ times</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              History of loan defaults (if any)
            </p>
            {errors.prior_default_history && <p className="text-sm text-red-500">{errors.prior_default_history}</p>}
          </div>
        </div>
      </div>

      {/* Information Box */}
      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
        <h4 className="font-medium text-green-900 mb-2">Why This Matters</h4>
        <ul className="text-sm text-green-800 space-y-1">
          <li>• Full repayment of past loans significantly improves your credit assessment</li>
          <li>• No defaults and consistent repayment indicates reliability</li>
          <li>• Repayment history directly maps to Yogyank scoring contract</li>
        </ul>
      </div>
    </div>
  );
};

export default Step6CreditHistory;
